/**
 * Shared Kenyan phone-number handling.
 *
 * Single source of truth for phone validation + E.164 normalisation across
 * every flow that captures a phone number (resident invite, recurring/staff/
 * domestic pass creation, guard walk-in registration). Previously this logic
 * was inlined in `InviteVisitorForm` and re-implemented (incorrectly, hardcoded
 * to `+254`) in the guard walk-in form — leading to inconsistent acceptance of
 * locally-typed `07…`/`01…` numbers.
 *
 * Accepted input shapes (after stripping spaces and dashes):
 *   - Local:        `0712345678`, `0112345678`
 *   - Country code: `254712345678`
 *   - E.164:        `+254712345678`
 *
 * Kenyan mobile prefixes start `7` (Safaricom/Airtel/Telkom) or `1` (newer
 * Safaricom ranges). The pattern intentionally mirrors the original
 * `InviteVisitorForm` regex so behaviour for already-valid inputs is unchanged.
 */

/** Matches a Kenyan phone in local, country-code, or E.164 form. */
export const KE_PHONE_PATTERN = /^(\+?254|0)[17]\d{7,9}$/;

/** Strip spaces and dashes; callers never need the raw separators. */
function strip(input: string): string {
  return input.replace(/[\s-]/g, '');
}

/**
 * Returns the canonical E.164 (`+254…`) form of a Kenyan phone number, or
 * `null` when the input cannot be normalised to a valid Kenyan number. Callers
 * surface the validation message when this returns `null`.
 */
export function normalizeKePhoneToE164(input: string): string | null {
  const stripped = strip(input);
  if (!KE_PHONE_PATTERN.test(stripped)) return null;

  // Local format: 07… or 01… → +254…
  if (/^0[17]/.test(stripped)) return '+254' + stripped.slice(1);
  // Country code without +: 254… → +254…
  if (/^254[17]/.test(stripped)) return '+' + stripped;
  // Already E.164 (+254…)
  return stripped;
}

/** True when the input is a valid Kenyan phone in any accepted shape. */
export function isValidKePhone(input: string): boolean {
  return normalizeKePhoneToE164(input) !== null;
}
